// src/contexts/RealtimeServiceContext.tsx

import React, { createContext, useContext, useRef, useCallback, useState, useEffect } from 'react';
import { UpdatedRealtimeClient } from '../realtime/client'
import { ItemType } from '@theodoreniu/realtime-api-beta/dist/lib/client.js';
import { RealtimeEvent } from '../utils/events.js';

import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { useAuth } from '../contexts/AuthContext';
import { useFabricService } from './FabricServiceContext';
import { useTool } from '../contexts/ToolContext';
import { nanoid } from 'nanoid';
import { instructions } from '../utils/conversation_config.js';
import { Voice, VOICE_OPTIONS } from '../types/Voice';
import { OverviewObject } from '../types/DataObject';

interface RealtimeServiceContextType {
    clientRef: React.MutableRefObject<UpdatedRealtimeClient>;
    wavRecorderRef: React.MutableRefObject<WavRecorder>;
    wavStreamPlayerRef: React.MutableRefObject<WavStreamPlayer>;
    startTimeRef: React.MutableRefObject<string>;
    realtimeEvents: RealtimeEvent[];
    isConnected: boolean;
    isConnecting: boolean;
    canPushToTalk: boolean;
    isRecording: boolean;
    items: ItemType[];
    voice: Voice;
    temperature: number;
    selectedItem: ItemType | undefined;
    selectedResponseId: string | undefined;
    setRealtimeEvents: React.Dispatch<React.SetStateAction<RealtimeEvent[]>>;
    setItems: React.Dispatch<React.SetStateAction<ItemType[]>>;
    setVoice: React.Dispatch<React.SetStateAction<Voice>>;
    setTemperature: React.Dispatch<React.SetStateAction<number>>;
    setSelectedItem: React.Dispatch<React.SetStateAction<ItemType | undefined>>;
    updateSelectedResponseId: () => Promise<void>;
    connectConversation: () => Promise<void>;
    disconnectConversation: () => Promise<void>;
    resetConversation: () => Promise<void>;
    refresh: () => Promise<void>;
    changeTurnEndType: (turnEndType: string) => Promise<void>;
    deleteConversationItem: (itemId: string) => Promise<void>;
    sendMessage: (message: string) => Promise<void>;
    startRecording: () => void;
    stopRecording: () => void;
    injectResponse: (context: string) => Promise<void>;
}

const RealtimeServiceContext = createContext<RealtimeServiceContextType | undefined>(undefined);

export const RealtimeServiceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    /**
     * Instantiate:
     * - WavRecorder (speech input)
     * - WavStreamPlayer (speech output)
     * - RealtimeClient (API client)
     */
    const wavRecorderRef = useRef<WavRecorder>(
        new WavRecorder({ sampleRate: 24000 })
    );
    const wavStreamPlayerRef = useRef<WavStreamPlayer>(
        new WavStreamPlayer({ sampleRate: 24000 })
    );

    const { isFabric, fabricInstructions, fabricVoice, createFabricResponse, fabricEvents, resetFabric, disconnectFabric, setFabricItems } = useFabricService()
    const { getClientConfig, apiKey } = useAuth();
    const clientRef = useRef<UpdatedRealtimeClient>(new UpdatedRealtimeClient(getClientConfig()))

    const startTimeRef = useRef<string>(new Date().toISOString());
    const processFabricEventsRef = useRef<number>(0)

    /**
     * All of our variables for displaying application state
     * - items are all conversation items (dialog)
     * - realtimeEvents are event logs, which can be expanded
     * - memoryKeyValue is for set_memory() function
     * - weatherCoordinates, weatherMarker are for get_weather() function
     */
    const [items, setItems] = useState<ItemType[]>([]);
    const itemsRef = useRef(items);
    const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [canPushToTalk, setCanPushToTalk] = useState(true);
    const [isRecording, setIsRecording] = useState(false);
    const [functionCallId, setFunctionCallId] = useState('');
    const [voice, setVoice] = useState(VOICE_OPTIONS[0]);
    const [temperature, setTemperature] = useState(0.8);
    const [selectedItem, setSelectedItem] = useState<ItemType>()
    const [selectedResponseId, setSelectedResponseId] = useState<string>()

    const {
        addWeatherTool,
        resetWeatherTool,
        addMemoryKeyValueTool,
        resetMemoryKeyValueTool,
        addControlLightsTools,
        resetControlLightsTools,
    } = useTool()

    /**
     * Connect to conversation:
     * WavRecorder taks speech input, WavStreamPlayer output, client is API client
     */
    const connectConversation = useCallback(async () => {
        const client = clientRef.current;
        const wavRecorder = wavRecorderRef.current;
        const wavStreamPlayer = wavStreamPlayerRef.current;

        setIsConnecting(true);

        try {
            // Set state variables
            startTimeRef.current = new Date().toISOString();
            setRealtimeEvents([]);
            setItems(client.conversation.getItems());

            // Connect to microphone
            await wavRecorder.begin();

            // Connect to audio output
            await wavStreamPlayer.connect();

            // Connect to realtime API
            await client.connect();

            if (client.getTurnDetectionType() === 'server_vad') {
                await wavRecorder.record((data) => client.appendInputAudio(data.mono));
            }
        } catch (error) {
            console.error('Connection failed:', error);
            setIsConnecting(false);
            setIsConnected(false);
        }
    }, []);

    /**
     * Disconnect and reset conversation state
     */
    const disconnectConversation = useCallback(async () => {
        setIsConnected(false);
        setRealtimeEvents([]);
        setItems([]);

        resetWeatherTool()
        resetMemoryKeyValueTool()
        resetControlLightsTools()

        const client = clientRef.current;
        try {
            client.disconnect();

            const wavRecorder = wavRecorderRef.current;
            await wavRecorder.end();

            const wavStreamPlayer = wavStreamPlayerRef.current;
            await wavStreamPlayer.interrupt();
        } catch (error) {
            window.location.reload()
        }
    }, []);

    /**
     * Refresh Realtime Session
     */
    useEffect(() => {
        itemsRef.current = items;
    }, [items]);

    const refresh = useCallback(async () => {
        const client = clientRef.current;
        client.realtime.disconnect()
        await client.connect();

        const currentItems = itemsRef.current;
        const filteredItems = currentItems.filter((item) => item.type === 'message')
        for (const item of filteredItems) {
            const outputMessage = {
                role: item.role,
                content: item.formatted.text === '' ? item.formatted.transcript : item.formatted.text
            }
            await injectContext('restore_conversation_message', outputMessage, 'history_')
        }
    }, []);

    useEffect(() => {
        if (apiKey) {
            clientRef.current = new UpdatedRealtimeClient(getClientConfig())
        }
    }, [apiKey, getClientConfig]);

    // Futures-Fabric: Reset conversation when changing mode
    const resetConversation = useCallback(async () => {
        if (isConnected) {
            _resetConversation()
            if (isFabric) {
                processFabricEventsRef.current = 0
                resetFabric()
            }
        }
    }, [isFabric, isConnected]);

    useEffect(() => {
        if (isFabric && !canPushToTalk) {
            setCanPushToTalk(true)
        }
        if (isConnected && !isFabric) {
            processFabricEventsRef.current = 0
            disconnectFabric()
        }
        resetConversation()
    }, [isFabric]);

    const _resetConversation = useCallback(async () => {
        await disconnectConversation();
        await connectConversation();
        const turn_type_reverse = !canPushToTalk ? 'none' : 'server_vad';
        await changeTurnEndType(turn_type_reverse)
        const turn_type = canPushToTalk ? 'none' : 'server_vad';
        await changeTurnEndType(turn_type)
    }, [canPushToTalk]);

    /**
     * Switch between Manual <> VAD mode for communication
     */
    const changeTurnEndType = async (value: string) => {
        const client = clientRef.current;
        const wavRecorder = wavRecorderRef.current;
        if (value === 'none' && wavRecorder.getStatus() === 'recording') {
            await wavRecorder.pause();
        }
        client.updateSession({
            turn_detection: value === 'none' ? null : { type: 'server_vad' },
        });
        if (value === 'server_vad' && client.isConnected()) {
            await wavRecorder.record((data) => client.appendInputAudio(data.mono));
        }
        setCanPushToTalk(value === 'none');
    };

    const deleteConversationItem = useCallback(async (id: string) => {
        const client = clientRef.current;
        client.deleteItem(id);
    }, []);

    const sendMessage = useCallback(async (message: string) => {
        const client = clientRef.current;
        client.sendUserMessageContent([
            {
                type: 'input_text',
                text: message,
            },
        ]);
    }, []);

    const beginResponseFunctionCall = async () => {
        const client = clientRef.current;
        if (!client) throw new Error('RealtimeClient is not initialized');
        const shortUuid = nanoid(10);
        const func_event = {
            "type": "conversation.item.create",
            "previous_item_id": null,
            "item": {
                "call_id": `call_${shortUuid}`,
                "name": "get_response_info",
                "type": "function_call",
                "arguments": "",
            }
        }
        client.realtime.send('conversation.item.create', func_event)

        setFunctionCallId(`call_${shortUuid}`)
    }

    // Futures-Fabric: Inject context into the conversation
    const injectResponse = async (transcript: string) => {
        const client = clientRef.current;
        if (!client) throw new Error('RealtimeClient is not initialized');
        transcript = transcript.trim();
        if (transcript.length === 0) {
            return;
        }

        await refresh()
        await beginResponseFunctionCall()

        createFabricResponse(transcript, (outputMessage) => { })
    };

    const injectContext = async (name: string, data = {}, prefix: string = '') => {
        const client = clientRef.current;
        if (!client) throw new Error('RealtimeClient is not initialized');

        const shortUuid = nanoid(10);
        const func_event = {
            "type": "conversation.item.create",
            "previous_item_id": null,
            "item": {
                "call_id": `${prefix}call_${shortUuid}`,
                "name": "restore_conversation_message",
                "type": "function_call",
                "arguments": "",
            }
        }
        client.realtime.send('conversation.item.create', func_event)

        const func_output_event = {
            "type": "conversation.item.create",
            "previous_item_id": null,
            "item": {
                "call_id": `${prefix}call_${shortUuid}`,
                "type": "function_call_output",
                "output": JSON.stringify(data),
            }
        }
        client.realtime.send('conversation.item.create', func_output_event)
    }

    useEffect(() => {
        const client = clientRef.current;
        const lastProcessedCountRef = processFabricEventsRef.current

        // Only process new events
        const newEvents = fabricEvents.slice(lastProcessedCountRef);

        newEvents.forEach((eventData) => {
            // Handle response output items
            if (eventData.event.type === 'fabric.response.output_item.added') {
                if (eventData.event.item.output[0].fabric_type !== 'response') return;

                const responseMessage = eventData.event.item.output[0].content;

                const outputMessage = {
                    "instructions": "You are being provided an approved response. Attempt to closely follow this guidance.",
                    "response": responseMessage
                };

                const func_output_event = {
                    "type": "conversation.item.create",
                    "previous_item_id": null,
                    "item": {
                        "call_id": functionCallId,
                        "type": "function_call_output",
                        "output": JSON.stringify(outputMessage),
                    }
                };

                client.realtime.send('conversation.item.create', func_output_event);
                client.createResponse();
            }
            // Handle conversation output items
            else if (eventData.event.type === 'fabric.conversation.item.output_item.added') {
                const outputMessage = {
                    "name": eventData.event.output_item.name,
                    "role": eventData.event.output_item.role,
                    "content": eventData.event.output_item.content
                };

                injectContext('restore_conversation_message', outputMessage, 'history_');

                client.realtime.receive('conversation.item.created', {
                    "event_id": `event_${nanoid(10)}`,
                    "type": "conversation.item.created",
                    "previous_item_id": null,
                    "item": {
                        "id": `history_${nanoid(10)}`,
                        "object": "realtime.item",
                        "type": "message",
                        "status": "completed",
                        "role": eventData.event.output_item.role,
                        "content": [
                            {
                                type: eventData.event.output_item.role === 'user' ? "input_text" : 'text',
                                text: eventData.event.output_item.content
                            }
                        ]
                    }
                });

                if (eventData.event.output_item.fabric_type === 'auto-reply') {
                    beginResponseFunctionCall();
                }
            }
        });

        // Update the last processed count
        processFabricEventsRef.current = fabricEvents.length;

    }, [fabricEvents, clientRef, processFabricEventsRef]);

    const updateSelectedResponseId = useCallback(async () => {
        const newId = selectedItem?.id ? selectedItem?.id : [...fabricEvents]
            .reverse()
            .find((evt) => ['fabric.response.created'].includes(evt.event.type))
            ?.event.response?.id;
        setSelectedResponseId(newId)
    }, [selectedItem, realtimeEvents, fabricEvents])

    useEffect(() => {

        const updateItems: ItemType[] = []

        const attachRealtimeItem = (item: ItemType) => {
            const realtimeItem = items.find((i) =>
                i.formatted.transcript?.trim() === item.formatted.text
            );
            if (realtimeItem) {
                item.formatted = { ...item.formatted, ...realtimeItem.formatted }
            }
        }

        const funcEvents = realtimeEvents.filter(evt =>
            ['conversation.item.created'].includes(evt.event.type) && ['function_call', 'function_call_output'].includes(evt.event.item.type)
        );

        [...funcEvents, ...fabricEvents].sort((a, b) => a.time.localeCompare(b.time)).forEach((event) => {
            switch (event.event.type) {
                case 'fabric.conversation.item.output_item.added':
                    const conversationItem = { ...event.event.output_item, formatted: { text: event.event.output_item.content } }
                    attachRealtimeItem(conversationItem)
                    updateItems.push(conversationItem)
                    break;
                case 'fabric.response.created':
                    const outputItems = event.event.response.output?.map((id: string) => {
                        return fabricEvents.find((e) => e.event.type === 'fabric.response.output_item.added' && e.event.item.id === id)
                    })

                    const validItems = outputItems
                        .filter((itemEvent: RealtimeEvent) => (
                            itemEvent &&
                            (event.event.response.status === "completed" ||
                                (itemEvent.event.item.output[0].recipient === 'overview')
                            )
                        ));

                    const formattedText = () => {
                        if (event.event.response.status === "completed") {
                            const responseItem = validItems.find((itemEvent: RealtimeEvent) => (
                                itemEvent.event.item.output[0].fabric_type === 'response'
                            ));
                            return responseItem?.event.item.output[0].content
                        }
                        if (validItems.length === 0 || !validItems.at(-1)) return '<i>Preparing</i> initial thoughts...'
                        if (validItems.at(-1)?.event.item.output[0].recipient !== 'overview') return validItems.at(-1)?.event.item.output[0].fabric_type
                        return `${(validItems.at(-1)?.event.item.output[0].content as OverviewObject).action_title}`
                    }

                    const text = formattedText()
                    const item = { ...event.event.response, role: 'assistant', formatted: { text } }
                    attachRealtimeItem(item)
                    updateItems.push(item)
                    break;
                case 'conversation.item.created':
                    const toolItem = items.find(item => item.id === event.event.item.id)
                    if (toolItem) {
                        updateItems.push(toolItem)
                    }
                    break;
                default:
                    break;
            }
        })

        setFabricItems(updateItems)

    }, [fabricEvents, items, realtimeEvents])

    /**
     * In push-to-talk mode, start recording
     * .appendInputAudio() for each sample
     */
    const startRecording = async () => {
        setIsRecording(true);
        const client = clientRef.current;
        const wavRecorder = wavRecorderRef.current;
        const wavStreamPlayer = wavStreamPlayerRef.current;
        const trackSampleOffset = await wavStreamPlayer.interrupt();
        if (trackSampleOffset?.trackId) {
            const { trackId, offset } = trackSampleOffset;
            await client.cancelResponse(trackId, offset);
        }
        await wavRecorder.record((data) => client.appendInputAudio(data.mono));
    };

    /**
     * In push-to-talk mode, stop recording
     */
    const stopRecording = async () => {
        setIsRecording(false);
        const client = clientRef.current;
        const wavRecorder = wavRecorderRef.current;
        await wavRecorder.pause();
        // Futures-Fabric: Update audio buffer without creating response
        if (client.inputAudioBuffer.byteLength > 0) {
            client.realtime.send('input_audio_buffer.commit', {});
            client.conversation.queueInputAudio(client.inputAudioBuffer);
            client.inputAudioBuffer = new Int16Array(0);
        }
    };

    /**
     * Core RealtimeClient and audio capture setup
     * Set all of our instructions, tools, events and more
     */
    useEffect(() => {
        // Get refs
        const wavStreamPlayer = wavStreamPlayerRef.current;
        const client = clientRef.current;

        // Set instructions
        // Set transcription, otherwise we don't get user transcriptions back
        client.updateSession({
            instructions: isFabric ? fabricInstructions : instructions,
            input_audio_transcription: { model: 'whisper-1' },
            voice: isFabric ? fabricVoice.value : voice.value,
            temperature: temperature,
        });

        // Add tools
        if (!isFabric) {
            addWeatherTool(client)
            addMemoryKeyValueTool(client)
            addControlLightsTools(client)
        }

        // handle realtime events from client + server for event logging
        client.on('realtime.event', async (realtimeEvent: RealtimeEvent) => {
            if (realtimeEvent.event.type === 'session.created') {
                setIsConnecting(false);
                setIsConnected(true);
            }
            else if (realtimeEvent.event.type === 'error' && realtimeEvent.event.error.code === "session_expired") {
                console.warn("Warning: Realtime API Error", realtimeEvent.event)
                refresh()
            }

            // Futures-Fabric: Cancel automatic responses
            if (realtimeEvent.event.type === 'conversation.item.created' && realtimeEvent.event.item.role === 'user') {
                if (isFabric === true) {
                    if (realtimeEvent.event.item.id.startsWith('history_')) {
                        return
                    }
                    client.cancelResponse("")
                    const itemContent = realtimeEvent.event.item.content[0] || undefined
                    if (itemContent?.type === 'input_text') {
                        await injectResponse(itemContent.text);
                    }
                }
            }

            // Futures-Fabric: Transcript of a user message is available
            if (realtimeEvent.event.type === 'conversation.item.input_audio_transcription.completed') {
                if (isFabric === true) {
                    await injectResponse(realtimeEvent.event.transcript);
                }
                else if (client.getTurnDetectionType() === null) {
                    client.createResponse()
                }
            }
            setRealtimeEvents((events) => events.concat(realtimeEvent));
        });
        client.on('error', (event: any) => { });
        client.on('conversation.interrupted', async () => {
            const trackSampleOffset = await wavStreamPlayer.interrupt();
            if (trackSampleOffset?.trackId) {
                const { trackId, offset } = trackSampleOffset;
                await client.cancelResponse(trackId, offset);
            }
        });
        client.on('conversation.updated', async ({ item, delta }: any) => {
            const items = client.conversation.getItems();
            if (delta?.audio) {
                wavStreamPlayer.add16BitPCM(delta.audio, item.id);
            }
            if (item.status === 'completed' && item.formatted.audio?.length) {
                const wavFile = await WavRecorder.decode(
                    item.formatted.audio,
                    24000,
                    24000
                );
                item.formatted.file = wavFile;
            }
            setItems(items);
        });

        setItems(client.conversation.getItems());

        return () => {
            // cleanup; resets to defaults
            client.reset();
        };
    }, [clientRef.current, isFabric, voice, fabricVoice, temperature]);

    return (
        <RealtimeServiceContext.Provider
            value={{
                clientRef,
                wavRecorderRef,
                wavStreamPlayerRef,
                startTimeRef,
                realtimeEvents,
                isConnected,
                isConnecting,
                canPushToTalk,
                items,
                isRecording,
                voice,
                temperature,
                selectedItem,
                selectedResponseId,
                setRealtimeEvents,
                setItems,
                setVoice,
                setTemperature,
                setSelectedItem,
                updateSelectedResponseId,
                connectConversation,
                disconnectConversation,
                resetConversation,
                refresh,
                changeTurnEndType,
                deleteConversationItem,
                sendMessage,
                startRecording,
                stopRecording,
                injectResponse
            }}
        >
            {children}
        </RealtimeServiceContext.Provider>
    );
};

export const useRealtimeService = () => {
    const context = useContext(RealtimeServiceContext);
    if (context === undefined) {
        throw new Error('useRealtimeService must be used within an RealtimeServiceProvider');
    }
    return context;
};
